import { useCallback } from "react";
import { useObservedElementRef, useResizeObserver, useFloatingScrollbar } from "@tellescope/react-hooks";

/**
 * Whether any descendant is itself a vertical scroller with content below its fold.
 *
 * The geometric test comes first deliberately: `getComputedStyle` is the expensive half and forces
 * style resolution per descendant, so `&&` short-circuits it away for everything that is not
 * overflowing.
 */
const hasOverflowingDescendant = (element: HTMLElement): boolean =>
  Array.from(element.querySelectorAll<HTMLElement>("*")).some(
    (child) =>
      child.scrollHeight > child.clientHeight + 1 && ["auto", "scroll"].includes(getComputedStyle(child).overflowY)
  );

export interface UseBlockContentArgs {
  /**
   * Whether the caller uses the overflow measurement. When false it is not taken at all: the walk
   * below reads computed style for every descendant on every resize, and in the `fill` layout — the
   * maximized view, so the largest lists — nothing consumes the result.
   */
  measureOverflow?: boolean;
}

export const useBlockContent = ({ measureOverflow = true }: UseBlockContentArgs = {}) => {
  const { scrollContainerRef, thumbRef, handleScroll } = useFloatingScrollbar<HTMLElement>();

  const { ref, value: isOverflowing } = useResizeObserver<boolean, HTMLElement>(
    (element) =>
      measureOverflow && (element.scrollHeight > element.clientHeight + 1 || hasOverflowingDescendant(element)),
    false
  );

  /**
   * The scrolling element itself, tracked as state so a virtualized child (`PaginatedList`) can treat
   * it as its scroll container instead of nesting a second one that could never overflow.
   *
   * State rather than a plain ref because the consumer is a descendant reached through context: a ref
   * mutation would not re-render it, leaving it to poll `.current` every render to notice the node.
   * This element also changes identity in normal use — `BlockContent` renders a `<div>` in edit mode
   * and a `<button>` otherwise — so the swap has to propagate.
   */
  const { ref: scrollElementRef, element: scrollElement } = useObservedElementRef();

  /**
   * Memoized because it composes refs that track element *identity*. An inline callback makes React
   * detach (`null`) and re-attach the node on every render, so `useObservedElementRef` would publish
   * two state changes per render and re-run the observer effect — re-walking the subtree in
   * `hasOverflowingDescendant` each time. The composed refs are all stable, so `[]` is exhaustive.
   */
  const scrollRef = useCallback(
    (element: HTMLElement | null) => {
      ref.current = element;
      scrollContainerRef.current = element;
      scrollElementRef.current = element;
    },
    [ref, scrollContainerRef, scrollElementRef]
  );

  return { isOverflowing, scrollRef, scrollElement, thumbRef, handleScroll };
};
